import {
  isValidElement,
  type Key,
  type PropsWithChildren,
  type ReactNode,
  type ReactElement,
} from 'react';

import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';

/**
 * @public
 */
export type SelectOptionProps<OptionValue> = PropsWithChildren &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps & {
    value: OptionValue;

    textValue?: string;

    id?: Key;

    'aria-label'?: string;

    disabled?: boolean;
  };

/**
 * Typeguard function that will check if a ReactNode is a Select.Option
 * @internal
 */
export function _isSelectOptionElement<T>(
  child: ReactNode,
): child is ReactElement<SelectOptionProps<T>> {
  return isValidElement(child) && child.type === SelectOption;
}

/**
 * `Select.Option` defines a single option within the select component.
 * `Select.Option` components are only valid within `Select.Content` or
 * `Select.Group` elements.
 *
 * @example
 * ```tsx
 * <Select.Content>
 *    <Select.Option value="red">Red</Select.Option>
 *    <Select.Option value="green">Green</Select.Option>
 *    <Select.Group>
 *      <Select.GroupLabel>Other colors</Select.GroupLabel>
 *      <Select.Option value="purple">Purple</Select.Option>
 *    <Select.Option value="pink">Pink</Select.Option>
 *    </Select.Group>
 * </Select.Content>
 * ```
 * @public
 */
export function SelectOption<OptionValue>(
  props: SelectOptionProps<OptionValue>,
): null {
  return null;
}
